import React, { useState, useEffect, useCallback } from 'react';
import { getFichajeEstado, ficharEntrada, ficharSalida, getFichajes } from '../services/api';
import './Fichajes.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function formatDuracion(minutos) {
  if (!minutos && minutos !== 0) return '—';
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  if (h === 0) return `${m}min`;
  return m === 0 ? `${h}h` : `${h}h ${m}min`;
}

const TZ = 'Europe/Madrid';

// El servidor devuelve ISO sin "Z" → forzamos UTC para la conversión correcta
function asUTC(isoStr) {
  if (!isoStr) return null;
  return isoStr.endsWith('Z') || isoStr.includes('+') ? isoStr : isoStr + 'Z';
}

function formatHora(isoStr) {
  if (!isoStr) return '—';
  return new Date(asUTC(isoStr)).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: TZ });
}

function formatFecha(isoStr) {
  if (!isoStr) return '—';
  return new Date(asUTC(isoStr)).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', timeZone: TZ });
}

function ElapsedTimer({ desde }) {
  const [elapsed, setElapsed] = useState('');

  useEffect(() => {
    const update = () => {
      const diff = Math.floor((Date.now() - new Date(asUTC(desde)).getTime()) / 1000);
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setElapsed(
        `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
      );
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [desde]);

  return <span className="fich-timer">{elapsed}</span>;
}

export default function Fichajes({ user }) {
  const [estado, setEstado] = useState(null);   // { fichado, fichaje }
  const [fichajes, setFichajes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [notas, setNotas] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filtroNombre, setFiltroNombre] = useState('');
  const [filtroDesde, setFiltroDesde] = useState('');
  const [filtroHasta, setFiltroHasta] = useState('');
  // can_gestionar viene del backend (admin, superadmin o cualquier rol con permiso 'fichaje.gestionar')
  const canVerTodos = estado?.can_gestionar ?? (user?.is_superadmin || user?.rol === 'administrador');
  const LIMIT = 20;

  const cargarEstado = useCallback(async () => {
    try {
      const res = await getFichajeEstado();
      setEstado(res.data);
    } catch {
      setEstado({ fichado: false, fichaje: null });
    }
  }, []);

  const filtroNombreRef = React.useRef(filtroNombre);
  const filtroDesdeRef = React.useRef(filtroDesde);
  const filtroHastaRef = React.useRef(filtroHasta);
  const canVerTodosRef = React.useRef(canVerTodos);

  filtroNombreRef.current = filtroNombre;
  filtroDesdeRef.current = filtroDesde;
  filtroHastaRef.current = filtroHasta;
  canVerTodosRef.current = canVerTodos;

  const cargarFichajes = useCallback(async (p = 1) => {
    try {
      const params = { page: p, limit: LIMIT };
      if (filtroDesdeRef.current) params.fecha_desde = filtroDesdeRef.current;
      if (filtroHastaRef.current) params.fecha_hasta = filtroHastaRef.current;
      if (canVerTodosRef.current && filtroNombreRef.current) params.nombre_usuario = filtroNombreRef.current;
      const res = await getFichajes(params);
      setFichajes(res.data.fichajes);
      setTotal(res.data.total);
    } catch {
      setFichajes([]);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([cargarEstado(), cargarFichajes(1)]).finally(() => setLoading(false));
  }, [cargarEstado, cargarFichajes]);

  const showMsg = (type, msg) => {
    if (type === 'success') { setSuccess(msg); setError(''); }
    else { setError(msg); setSuccess(''); }
    setTimeout(() => { setSuccess(''); setError(''); }, 4000);
  };

  const getUbicacion = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      pos => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      () => resolve(null),
      { timeout: 10000, maximumAge: 0, enableHighAccuracy: true }
    );
  });

  const handleEntrada = async () => {
    setActionLoading(true);
    try {
      const ubi = await getUbicacion();
      await ficharEntrada(notas, ubi?.lat ?? null, ubi?.lon ?? null);
      setNotas('');
      showMsg('success', 'Entrada registrada correctamente');
      await Promise.all([cargarEstado(), cargarFichajes(1)]);
      setPage(1);
    } catch (e) {
      showMsg('error', e.response?.data?.error || 'Error al fichar entrada');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSalida = async () => {
    setActionLoading(true);
    try {
      const ubi = await getUbicacion();
      await ficharSalida(notas, ubi?.lat ?? null, ubi?.lon ?? null);
      setNotas('');
      showMsg('success', 'Salida registrada correctamente');
      await Promise.all([cargarEstado(), cargarFichajes(1)]);
      setPage(1);
    } catch (e) {
      showMsg('error', e.response?.data?.error || 'Error al fichar salida');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFiltrar = (e) => {
    e.preventDefault();
    setPage(1);
    cargarFichajes(1);
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filtroDesde) params.append('fecha_desde', filtroDesde);
      if (filtroHasta) params.append('fecha_hasta', filtroHasta);
      if (canVerTodos && filtroNombre) params.append('nombre_usuario', filtroNombre);
      const url = `${API_URL}/fichajes/export?${params.toString()}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `fichajes_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch {
      showMsg('error', 'Error al exportar fichajes');
    }
  };

  const handlePage = (p) => {
    setPage(p);
    cargarFichajes(p);
  };

  const totalPages = Math.ceil(total / LIMIT);
  const hoy = new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: TZ });

  if (loading) {
    return (
      <div className="fich-page">
        <div className="loading-container"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="fich-page">
      {/* Cabecera */}
      <div className="fich-header">
        <div>
          <h1 className="fich-title">Fichajes</h1>
          <p className="fich-subtitle">{hoy.charAt(0).toUpperCase() + hoy.slice(1)}</p>
        </div>
      </div>

      {/* Mensajes */}
      {error && <div className="fich-alert fich-alert--error">{error}</div>}
      {success && <div className="fich-alert fich-alert--success">{success}</div>}

      {/* Tarjeta de fichaje */}
      <div className={`fich-card ${estado?.fichado ? 'fich-card--activo' : 'fich-card--libre'}`}>
        <div className="fich-card-status">
          <div className={`fich-status-dot ${estado?.fichado ? 'fich-status-dot--on' : ''}`} />
          <span className="fich-status-label">
            {estado?.fichado ? 'En jornada' : 'Sin fichar'}
          </span>
        </div>

        {estado?.fichado && estado.fichaje ? (
          <div className="fich-card-info">
            <div className="fich-card-desde">
              Entrada a las <strong>{formatHora(estado.fichaje.fecha_entrada)}</strong>
            </div>
            <ElapsedTimer desde={estado.fichaje.fecha_entrada} />
          </div>
        ) : (
          <div className="fich-card-info">
            <p className="fich-card-hint">Registra tu entrada para iniciar la jornada</p>
          </div>
        )}

        <div className="fich-notas-row">
          <input
            className="fich-notas-input"
            type="text"
            placeholder="Notas opcionales..."
            value={notas}
            onChange={e => setNotas(e.target.value)}
            maxLength={200}
          />
        </div>

        <button
          className={`fich-btn ${estado?.fichado ? 'fich-btn--salida' : 'fich-btn--entrada'}`}
          onClick={estado?.fichado ? handleSalida : handleEntrada}
          disabled={actionLoading}
        >
          {actionLoading ? (
            <span className="fich-btn-spinner" />
          ) : estado?.fichado ? (
            <>
              <span className="fich-btn-icon">🔴</span>
              Fichar Salida
            </>
          ) : (
            <>
              <span className="fich-btn-icon">🟢</span>
              Fichar Entrada
            </>
          )}
        </button>
      </div>

      {/* Filtros */}
      <div className="fich-section">
        <div className="fich-section-header">
          <h2 className="fich-section-title">Historial de fichajes</h2>
          <button className="btn btn-secondary btn-sm fich-export-btn" type="button" onClick={handleExport}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:6}}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Descargar Excel
          </button>
        </div>
        <form className="fich-filtros" onSubmit={handleFiltrar}>
          {canVerTodos && (
            <input
              className="fich-filtro-input"
              type="text"
              placeholder="Buscar por nombre..."
              value={filtroNombre}
              onChange={e => setFiltroNombre(e.target.value)}
            />
          )}
          <input
            className="fich-filtro-input"
            type="date"
            value={filtroDesde}
            onChange={e => setFiltroDesde(e.target.value)}
            title="Desde"
          />
          <input
            className="fich-filtro-input"
            type="date"
            value={filtroHasta}
            onChange={e => setFiltroHasta(e.target.value)}
            title="Hasta"
          />
          <button className="btn btn-primary btn-sm" type="submit">Filtrar</button>
          <button
            className="btn btn-secondary btn-sm"
            type="button"
            onClick={() => { setFiltroDesde(''); setFiltroHasta(''); setFiltroNombre(''); setTimeout(() => cargarFichajes(1), 0); }}
          >
            Limpiar
          </button>
        </form>
      </div>

      {/* Tabla */}
      <div className="fich-table-wrapper">
        {fichajes.length === 0 ? (
          <div className="fich-empty">No hay fichajes registrados</div>
        ) : (
          <table className="fich-table">
            <thead>
              <tr>
                <th>Fecha</th>
                {canVerTodos && <th>Usuario</th>}
                <th>Entrada</th>
                <th>Salida</th>
                <th>Duración</th>
                <th>Ubicación</th>
                <th>Notas</th>
              </tr>
            </thead>
            <tbody>
              {fichajes.map(f => (
                <tr key={f.id} className={!f.fecha_salida ? 'fich-row--activo' : ''}>
                  <td>{formatFecha(f.fecha_entrada)}</td>
                  {canVerTodos && <td className="fich-usuario-cell">{f.usuario_nombre || `ID ${f.usuario_id}`}</td>}
                  <td>{formatHora(f.fecha_entrada)}</td>
                  <td>{f.fecha_salida ? formatHora(f.fecha_salida) : <span className="fich-badge-activo">En curso</span>}</td>
                  <td>{f.duracion_minutos != null ? formatDuracion(f.duracion_minutos) : (f.fecha_salida ? '—' : <span className="fich-badge-activo">—</span>)}</td>
                  <td className="fich-ubi-cell">
                    {f.latitud_entrada && f.longitud_entrada ? (
                      <a
                        href={`https://maps.google.com/?q=${f.latitud_entrada},${f.longitud_entrada}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="fich-ubi-link"
                        title={`Entrada: ${parseFloat(f.latitud_entrada).toFixed(5)}, ${parseFloat(f.longitud_entrada).toFixed(5)}`}
                      >📍 Entrada</a>
                    ) : null}
                    {f.latitud_salida && f.longitud_salida ? (
                      <a
                        href={`https://maps.google.com/?q=${f.latitud_salida},${f.longitud_salida}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="fich-ubi-link"
                        title={`Salida: ${parseFloat(f.latitud_salida).toFixed(5)}, ${parseFloat(f.longitud_salida).toFixed(5)}`}
                      >📍 Salida</a>
                    ) : null}
                    {!f.latitud_entrada && !f.latitud_salida ? '—' : null}
                  </td>
                  <td className="fich-notas-cell">{f.notas || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="fich-pagination">
          <button className="btn btn-secondary btn-sm" disabled={page === 1} onClick={() => handlePage(page - 1)}>← Anterior</button>
          <span className="fich-page-info">Página {page} de {totalPages} ({total} registros)</span>
          <button className="btn btn-secondary btn-sm" disabled={page === totalPages} onClick={() => handlePage(page + 1)}>Siguiente →</button>
        </div>
      )}
    </div>
  );
}
